import { intl } from 'lang/intl'
import React, { useCallback } from 'react'
import { FormattedMessage } from 'react-intl'

import * as TtForms from '@23/react-forms'

import { useModal } from '../../../Layout/Modal'
import {
  useDevices,
  useHasGrantedDevicesPermission,
  useSelectedDevices,
} from '../../useDevices'

import { VideoPreview } from './VideoPreview/VideoPreview'
import { AudioPreview } from './AudioPreview/AudioPreview'

import './DeviceSelector.scss'

type FormValues = {
  videoinput: string | ''
  audioinput: string | ''
  audiooutput: string | ''
}

interface DeviceSelectorProps {
  onSubmit: (values: FormValues) => void
}

const DeviceSelector = ({ onSubmit }: DeviceSelectorProps) => {
  const { closeModal } = useModal()
  const devicesState = useSelectedDevices()
  const devices = useDevices()
  const hasMediaPermissions = useHasGrantedDevicesPermission()

  const isAudioOutputSupported =
    typeof HTMLMediaElement.prototype.setSinkId === 'function'

  return (
    <div className="device-selector-modal">
      {!hasMediaPermissions && (
        <div className="device-selector-permissions-note">
          <FormattedMessage defaultMessage="Please grant microphone and camera access to view the list of devices." />
        </div>
      )}
      <TtForms.Form<FormValues>
        className="device-selector"
        label={intl.formatMessage({ defaultMessage: 'Device Selector' })}
        initialValues={{
          videoinput: devicesState.videoinput ?? '',
          audioinput: devicesState.audioinput ?? '',
          audiooutput: devicesState.audiooutput ?? '',
        }}
        enableReinitialize
        onSubmit={(formValues) => {
          onSubmit(formValues)
          closeModal()
        }}
      >
        {(formik) => (
          <>
            <div className="device-selector-preview">
              <VideoPreview deviceId={formik.values.videoinput} />
              <AudioPreview deviceId={formik.values.audioinput} />
            </div>
            <div className="device-selector-form">
              <TtForms.Select<FormValues>
                name="videoinput"
                label={intl.formatMessage({ defaultMessage: 'Camera' })}
                hoist
              >
                <TtForms.Option<FormValues>
                  name="videoinput"
                  value=""
                  label={intl.formatMessage({
                    defaultMessage: 'No device selected',
                  })}
                />
                {devices.videoinput.map((d) => (
                  <TtForms.Option<FormValues>
                    key={d.deviceId}
                    name="videoinput"
                    value={d.deviceId}
                    label={d.label}
                  />
                ))}
              </TtForms.Select>
              <TtForms.Select<FormValues>
                name="audioinput"
                label={intl.formatMessage({ defaultMessage: 'Microphone' })}
                hoist
              >
                <TtForms.Option<FormValues>
                  name="audioinput"
                  value=""
                  label={intl.formatMessage({
                    defaultMessage: 'No device selected',
                  })}
                />
                {devices.audioinput.map((d) => (
                  <TtForms.Option<FormValues>
                    key={d.deviceId}
                    name="audioinput"
                    value={d.deviceId}
                    label={d.label}
                  />
                ))}
              </TtForms.Select>
              {isAudioOutputSupported && (
                <TtForms.Select<FormValues>
                  name="audiooutput"
                  label={intl.formatMessage({
                    defaultMessage: 'Speaker and headphones',
                  })}
                  hoist
                >
                  <TtForms.Option<FormValues>
                    name="audiooutput"
                    value=""
                    label={intl.formatMessage({
                      defaultMessage: 'No device selected',
                    })}
                  />
                  {devices.audiooutput.map((d) => (
                    <TtForms.Option<FormValues>
                      key={d.deviceId}
                      name="audiooutput"
                      value={d.deviceId}
                      label={d.label}
                    />
                  ))}
                </TtForms.Select>
              )}
              <TtForms.SubmitButton
                className="device-selector-form-submit"
                disabled={false}
                pill
                onCancel={closeModal}
              >
                <FormattedMessage defaultMessage="Choose devices" />
              </TtForms.SubmitButton>
            </div>
          </>
        )}
      </TtForms.Form>
    </div>
  )
}

const useOpenDeviceSelector = () => {
  const { openModal } = useModal()

  return useCallback(() => {
    return new Promise<FormValues>((resolve) => {
      openModal({
        body: <DeviceSelector onSubmit={resolve} />,
        label: intl.formatMessage({ defaultMessage: 'Change devices' }),
        closable: false,
      })
    })
  }, [openModal])
}

export { useOpenDeviceSelector }
